import { NgClass } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input, output } from '@angular/core';

import moment from 'moment-timezone';
import { MomentModule } from 'ngx-moment';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { Task } from '@recruitee/tasks-types';
import {
  AdminsAvatarsComponent,
  CheckboxComponent,
  DateComponent,
  IconComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { TaskTitleWrapUrlsPipe } from './task-title-wrap-urls.pipe';

@Component({
  selector: 'rt-candidate-profile-task-item',
  templateUrl: './candidate-profile-task-item.component.html',
  styleUrls: ['./candidate-profile-task-item.component.less'],
  host: {
    '(click)': 'editTask()',
  },
  imports: [
    CheckboxComponent,
    TaskTitleWrapUrlsPipe,
    AdminsAvatarsComponent,
    DateComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
    MomentModule,
    NgClass,
  ],
})
export class CandidateProfileTaskItemComponent implements OnChanges {
  @Input() public task: Task;
  @Input() public adminTimezone: string;

  public loading = false;
  public dueDate: moment.Moment;
  public dueDateTooltipFormat: string = 'lll';
  public dateClass: string | null = null;
  public humanizedDate: string;

  public openTaskEditDialog = output<Task>();
  public taskToggle = output<{
    task: Task;
    completed: boolean;
  }>();

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if ('task' in changes) {
      this.setDueDateCopies();
    }
  }

  public editTask(): void {
    this.openTaskEditDialog.emit(this.task);
  }

  public toggle() {
    this.loading = !this.loading;
    this.taskToggle.emit({ task: this.task, completed: !this.task.completed });
    this.cdRef.detectChanges();
  }

  private setDueDateCopies() {
    this.dueDate = moment.tz(this.task.dueDate, this.adminTimezone || moment.tz.guess());
    const dateNow = moment.utc().tz(this.adminTimezone || moment.tz.guess()) || moment();
    const diff = this.dueDate.diff(dateNow, 'days', true);
    let dateClass: string | null = null;

    if (Math.abs(diff) < 1) {
      if (this.dueDate.date() === dateNow.date()) {
        dateClass = 'today';
      } else if (diff < 0) {
        dateClass = 'yesterday';
      } else if (diff > 0) {
        dateClass = 'tomorrow';
      }
    } else if (Math.abs(diff) < 2) {
      if (diff < 0 && this.dueDate.date() === dateNow.subtract(1, 'day').date()) {
        dateClass = 'yesterday';
      } else if (diff > 0 && this.dueDate.date() === dateNow.add(1, 'day').date()) {
        dateClass = 'tomorrow';
      }
    }

    this.dateClass = dateClass;
    this.humanizedDate = dateClass
      ? this.i18n.translate(`app.common.${dateClass}`)
      : this.dueDate.format('DD MMM YYYY');
  }
}
